import React, { useState } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert, Accordion } from 'react-bootstrap';
import '../../style.css';
import FloatingAvatar from '../avatar';

const Main = ({ model = "gemini", selectedDomain }) => {
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [textQuery, setTextQuery] = useState("");
    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [queryTags, setQueryTags] = useState(null);
    const [queryDescription, setQueryDescription] = useState(null);
    const [processTime, setProcessTime] = useState(null);

    const [activeAvatar, setActiveAvatar] = useState(null);
    const [resultLimit, setResultLimit] = useState(10);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
            setMatches([]);
            setQueryTags(null);
            setQueryDescription(null);
            setProcessTime(null);
            setError(null);
        }
    };

    const handleTextChange = (e) => {
        setTextQuery(e.target.value);
    };

    const clearImage = () => {
        setSelectedImage(null);
        setImagePreview(null);
        setQueryTags(null);
        setQueryDescription(null);
        setProcessTime(null);
    };

    const handleViewImage = async (url) => {
        try {
            const response = await fetch(url);
            if (!response.ok) throw new Error('Network response was not ok');
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            window.open(blobUrl, '_blank');
        } catch (err) {
            console.error('Error opening image:', err);
            window.open(url, '_blank'); // Fallback
        }
    };

    const findMatches = async (e) => {
        if (e) e.preventDefault();
        if (!selectedImage && !textQuery.trim()) return;

        setProcessTime(null);
        setError(null);
        setLoading(true);
        const formData = new FormData();
        if (selectedImage) {
            formData.append('image', selectedImage);
        }
        if (textQuery.trim()) {
            formData.append('text_query', textQuery);
        }
        formData.append('result_limit', resultLimit);
        // formData.append('model', model);

        try {
            // const response = await fetch('/api/find_match', {
            //     method: 'POST',
            //     body: formData,
            // });
            const response = await fetch(`/api/find_match/${model}`, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                throw new Error('Failed to fetch matches');
            }

            const data = await response.json();
            setMatches(data.points || []);
            setQueryTags(data.query_tags || null);
            setQueryDescription(data.query_description || null);
            setProcessTime(data.processing_time || null);
        } catch (err) {
            // setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container fluid className="px-2 px-md-4 position-relative" style={{ height: 'calc(100vh - 90px)', overflow: 'hidden' }}>
            <div className="d-flex justify-content-end align-items-center gap-2 position-md-absolute pb-lg-0 pb-2">
                        <label className="text-muted fw-semibold mb-0" style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>Result Limit:</label>
                        <select
                            className="form-select form-select-sm"
                            style={{ width: '70px', fontSize: '13px' }}
                            value={resultLimit}
                            onChange={(e) => setResultLimit(Number(e.target.value))}
                        >
                            {[10, 15, 20, 25, 30, 35, 40, 45, 50].map(n => (
                                <option key={n} value={n}>{n}</option>
                            ))}
                        </select>
                    </div>
            <Row className="g-4 h-100">
                {/* Left Column: Avatar */}
                <Col lg={3} md={5} style={{ height: '100%' }}>
                    <div className="left-section-panel" style={{ height: '100%' }}>
                        <FloatingAvatar 
                            activeSession={activeAvatar}
                            onSessionStart={() => {
                                setActiveAvatar('avatar');
                            }}
                            onSessionEnd={() => setActiveAvatar(null)}
                            matches={matches}
                            setMatches={setMatches} 
                            setQueryTags={setQueryTags} 
                            setQueryDescription={setQueryDescription} 
                            setProcessTime={setProcessTime}
                            selectedDomain={selectedDomain}
                            resultLimit={resultLimit}
                        />
                    </div>
                </Col>

                {/* Right Column: Advanced Search & Results */}
                <Col lg={9} md={7} className="thin-scrollbar" style={{ height: '100%', overflowY: 'auto', paddingRight: '12px' }}>
                    <div className="d-flex justify-content-center mb-4">
                        <div style={{ width: '60%', minWidth: '350px' }}>
                            <Accordion className="custom-accordion">
                                <Accordion.Item eventKey="0">
                                    <Accordion.Header>
                                        <span className="d-flex w-100 justify-content-center align-items-center gap-2" style={{ fontSize: '14px' }}>
                                            <span>Advanced Search (Manual Query & Upload)</span>
                                        </span>
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        <Card className="advanced-search-card py-4 gap-3 border-0">
                                            <h5 className="fw-bold mb-3 text-center" style={{ color: 'var(--primary-color)' }}>Manual Search</h5>
                                            
                                            <div className="search-container shadow-sm p-2 mb-3 bg-white rounded-pill d-flex align-items-center border">
                                                <input 
                                                    type="text" 
                                                    className="border-0 outline-none bg-transparent px-3 py-2 flex-grow-1" 
                                                    style={{ minWidth: 0 }}
                                                    placeholder="Describe jewelry... (e.g. 'diamond ring')" 
                                                    value={textQuery}
                                                    onChange={handleTextChange}
                                                    onKeyDown={(e) => e.key === 'Enter' && findMatches()}
                                                    disabled={loading}
                                                />
                                                <Button 
                                                    className="btn-primary-custom rounded-pill px-3"
                                                    onClick={findMatches}
                                                    disabled={loading || (!textQuery.trim() && !selectedImage)}>
                                                    {loading && textQuery.trim() && !selectedImage ? (
                                                        <Spinner animation="border" size="sm"/>
                                                    ) : 'Search'}
                                                </Button>
                                            </div>

                                            <div className="upload-dropzone">
                                                <input 
                                                    type="file" 
                                                    accept="image/*" 
                                                    onChange={handleImageChange} 
                                                    disabled={loading}
                                                />
                                                <div className="py-2 text-center">
                                                    {imagePreview ? (
                                                        <div style={{ position: 'relative', display: 'inline-block' }}>
                                                            <img 
                                                                src={imagePreview} 
                                                                alt="Preview" 
                                                                style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '8px' }} 
                                                            />
                                                            <button
                                                                onClick={(e) => { e.preventDefault(); clearImage(); }}
                                                                disabled={loading}
                                                                style={{
                                                                    position: 'absolute',
                                                                    top: '-10px',
                                                                    right: '-10px',
                                                                    background: '#767575',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                    borderRadius: '50%',
                                                                    width: '24px',
                                                                    height: '24px',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    cursor: 'pointer',
                                                                    fontSize: '14px',
                                                                    fontWeight: 'bold',
                                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                                                    zIndex: 10
                                                                }}
                                                                title="Remove Image"
                                                            >
                                                                ✕
                                                            </button>
                                                        </div>
                                                    ) : (
                                                         <div className="text-muted">
                                                            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>+</div>
                                                            <p className="mb-0">Click or drag image to upload</p>
                                                            <small>JPG, PNG or WEBP</small>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {selectedImage && (
                                                <div className="text-center mt-3">
                                                    <Button 
                                                        onClick={findMatches} 
                                                        disabled={loading}
                                                        className="btn-primary-custom px-4">
                                                        {loading ? (
                                                            <>
                                                                <Spinner animation="border" size="sm" className="me-2" />
                                                                Searching...
                                                            </>
                                                        ) : 'Find Matches'}
                                                    </Button>
                                                </div>
                                            )}

                                            {queryTags && (queryTags.category || queryTags.type || (queryTags.tags && queryTags.tags.length > 0)) && (
                                                <div className="text-center mt-4 p-3 bg-white bg-opacity-50 rounded border">
                                                    {(queryTags.category || queryTags.type) && (
                                                        <div className="d-flex flex-wrap justify-content-center gap-2 mb-2">
                                                            {queryTags.category && (
                                                                <span className="badge bg-primary bg-opacity-10 text-primary border border-primary px-3 py-2 fw-semibold">
                                                                    {queryTags.category.charAt(0).toUpperCase() + queryTags.category.slice(1)}
                                                                </span>
                                                            )}
                                                            {queryTags.type && (
                                                                <span className="badge bg-success bg-opacity-10 text-success border border-success px-3 py-2 fw-semibold">
                                                                    {queryTags.type.charAt(0).toUpperCase() + queryTags.type.slice(1)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                    {queryTags.tags && queryTags.tags.length > 0 && (
                                                        <>
                                                            <h6 className="text-muted mb-2 small fw-bold">Detected Tags</h6>
                                                            <div className="d-flex flex-wrap justify-content-center gap-2">
                                                                {Array.from(new Set(queryTags.tags.flatMap(t => String(t).split(',').map(s => s.trim())).filter(Boolean))).map((tag, idx) => (
                                                                    <span key={idx} className="badge bg-white text-dark border px-3 py-2 fw-normal shadow-sm">
                                                                        {tag.charAt(0).toUpperCase() + tag.slice(1).toLowerCase()}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </>
                                                    )}
                                                    {queryDescription && (
                                                        <div className="mt-4 pt-3 border-top text-center">
                                                            <h6 className="text-muted mb-2 small fw-bold">Description</h6>
                                                            <p className="small text-muted mb-0">{queryDescription}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {processTime !== null && (
                                                <div className="d-flex justify-content-end mt-2">
                                                    <small className="text-muted">Processed in {processTime}s</small>
                                                </div>
                                            )}
                                        </Card>
                                    </Accordion.Body>
                                </Accordion.Item>
                            </Accordion>
                        </div>
                    </div>

                    {/* {error && (
                        <Alert variant="danger" className="text-center mb-4">
                            {error}
                        </Alert>
                    )} */}

                  {matches.length > 0 ? (
                        <div>
                            <h4 className="section-title my-4">Search Results ({matches.length})</h4>
                            <Row xs={1} sm={2} md={2} lg={3} className="g-4">
                                {matches.map((match, index) => {
                                    const imgPath = match.payload?.image_path;
                                    const imgName = match.payload?.image_name;
                                    const imgSrc = ((imgPath?.startsWith('http') || imgPath?.startsWith('https')) && !imgPath.includes('Images'))
                                        ? imgPath: `/api/Images/${imgName}`;
                                    const rawTags = match.payload?.tags || [];
                                    const tags = Array.from(new Set(rawTags.flatMap(t => String(t).split(',').map(s => s.trim())).filter(Boolean)));
                                    return (
                                        <Col key={index}>
                                            <Card className="match-card shadow-sm p-2 overflow-y">
                                                <div className="match-img-wrapper" onClick={() => handleViewImage(imgSrc)}>
                                                    <Card.Img variant="top" src={imgSrc} />
                                                    {tags.length > 0 && (
                                                        <div className="img-overlay">
                                                            <div className="tags-container">
                                                                {tags.map((tag, i) => (
                                                                    <span key={i} className="tag-badge">{tag.charAt(0).toUpperCase() + tag.slice(1).toLowerCase()}</span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                <Card.Body className="p-2 pt-3 d-flex flex-column justify-content-between text-start">
                                                    <h6 
                                                        className="fw-bold mb-2" 
                                                        style={{ 
                                                            fontSize: '0.9rem',
                                                            color: '#1e293b',
                                                            lineHeight: '1.3',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            minHeight: '2.4em',
                                                            cursor: 'pointer',
                                                            letterSpacing: '0.2px'
                                                        }}
                                                        title={match.payload?.image_name || 'Artwork'}
                                                        onClick={() => handleViewImage(imgSrc)}
                                                    >
                                                        {match.payload?.image_name || "Untitled Artwork"}
                                                    </h6>
                                                    <div className="d-flex justify-content-end align-items-center gap-2">
                                                        {activeAvatar && (
                                                            <Button
                                                                variant="outline-dark"
                                                                size="sm"
                                                                className="py-0 px-2 rounded-pill fw-bold"
                                                                style={{ fontSize: '0.75rem' }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    window.dispatchEvent(new CustomEvent('tell_info_agent', { detail: { imageName: match.payload?.image_name } }));
                                                                }}
                                                                title={`Ask ${selectedDomain?.avatar_name?.split('-')[1] || 'Meera'} about this item`}
                                                            >
                                                                <i className="bi bi-info-circle-fill me-1"></i> Tell More
                                                            </Button>
                                                        )}
                                                        {match.payload?.model ? (
                                                            <span className="badge rounded-pill bg-secondary mb-0" style={{ fontSize: '0.75em' }}>
                                                                {match.payload.model.charAt(0).toUpperCase() + match.payload.model.slice(1)}</span>
                                                        ): (
                                                            <span className="badge rounded-pill bg-secondary mb-0" style={{ fontSize: '0.75em' }}>
                                                                Qwen</span>
                                                        )}
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    );
                                })}
                            </Row>
                        </div>
                    ) : (
                        <div className="text-center bg-light border right-section-panel position-relative text-muted d-flex flex-column justify-content-center align-items-center">
                            <i className="bi bi-search fs-1 mb-2 text-secondary"></i>
                            <p className="mb-0">
                                {loading ? 'Searching for matches...' : (selectedImage || textQuery) ? 'No matches found yet. Try adjusting your search.' : 'Search results will appear here.'}
                            </p>
                        </div>
                    )}
                </Col>
            </Row>
        </Container>
    );
};

export default Main;
